import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { apiClient as defaultClient, type ApiClient, type ProjectSummary } from '../api/client';
import { useOrg } from '../org/context';
import { ProjectContext, type ProjectContextValue, type ProjectStatus } from './context';

export type { ProjectContextValue, ProjectStatus } from './context';

const PROJECT_PATH = /^\/projects\/([^/]+)/;

export interface ProjectProviderProps {
  children: ReactNode;
  /** Injectable client (tests). */
  client?: ApiClient;
  /** Seed state (tests). */
  initialProject?: ProjectSummary | null;
  initialStatus?: ProjectStatus;
}

/**
 * Loads the project for the active `/projects/:id/...` route so the persistent
 * chrome (breadcrumbs, header) can show which project is being viewed (#434). Loads
 * on org + project-id availability; clears off a project route. A stale project is
 * dropped the moment the id changes so the name never lags behind the route.
 */
export function ProjectProvider({
  children,
  client = defaultClient,
  initialProject = null,
  initialStatus = 'idle',
}: ProjectProviderProps) {
  const { currentOrg } = useOrg();
  const { pathname } = useLocation();
  const orgId = currentOrg?.id ?? null;
  const segment = PROJECT_PATH.exec(pathname)?.[1];
  const projectId = segment && segment !== 'new' ? segment : null;

  const [status, setStatus] = useState<ProjectStatus>(initialStatus);
  const [currentProject, setCurrentProject] = useState<ProjectSummary | null>(initialProject);
  const [error, setError] = useState<unknown>(null);

  const load = useCallback(async (): Promise<ProjectSummary | null> => {
    if (!orgId || !projectId) {
      setCurrentProject(null);
      setStatus('idle');
      setError(null);
      return null;
    }
    // Drop a stale project immediately so the name never lags the route.
    setCurrentProject((prev) => (prev && prev.id === projectId ? prev : null));
    setStatus('loading');
    setError(null);
    try {
      const project = await client.getProject(orgId, projectId);
      setCurrentProject(project);
      setStatus('ready');
      return project;
    } catch (err) {
      setError(err);
      setStatus('error');
      return null;
    }
  }, [client, orgId, projectId]);

  useEffect(() => {
    let active = true;
    void load().catch(() => {
      if (active) setStatus('error');
    });
    return () => {
      active = false;
    };
  }, [load]);

  const value = useMemo<ProjectContextValue>(
    () => ({ status, currentProject, error, refresh: load }),
    [status, currentProject, error, load],
  );

  return <ProjectContext.Provider value={value}>{children}</ProjectContext.Provider>;
}
